import { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.jsx'
import { installWidgetShadowDomIdLookup } from './lib/shadowDomDocumentPatch.js'
import widgetCss from './styles.css?inline'
import { tryScheduleActiveSessionTracking } from './lib/activeSessionTracking.js'
import { fetchWidgetConfig, widgetConfigDeviceType } from './lib/widgetConfigClient.js'
import {
  buildUnifiedMountQueue,
  mergeEnvelopeWithInstance,
  prepareWidgetMountHost,
  shouldSkipWidgetMountForPayload,
} from './lib/unifiedWidgetMounts.js'
import {
  startViewportOverflowAnchor,
  stopViewportOverflowAnchor,
} from './lib/viewportOverflowAnchor.js'

/** Match v1 breakpoint (768px): narrow viewport uses mobile layout rules. */
function isViewportMobileLayout() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 767px)').matches
}

/** Builder preview passes an explicit deviceType; prefer that over viewport width. */
function resolveIsMobile(inlineConfig) {
  const deviceType = String(inlineConfig?.deviceType || '')
    .trim()
    .toLowerCase()
  if (deviceType === 'mobile') return true
  if (deviceType === 'desktop') return false
  return isViewportMobileLayout()
}
import { normalizeConfig } from './lib/normalizeConfig.js'
import { bindVisitorIdToConf } from './lib/ometricsVisitorId.js'
import { sanitizeCustomCss } from './lib/sanitizeCss.js'
import { resolveTemplate, applyTemplateToHost } from './templates/index.js'
import { applyConfThemeVarsToHost } from './lib/widgetThemeVars.js'
import { generateUuid } from './lib/uuid.js'
import { sanitizeWidgetBubbleIconSvg } from './lib/sanitizeBubbleIcon.js'

installWidgetShadowDomIdLookup()

const GLOBAL_KEY = 'OmetricsAI'
const ROOTS_KEY = 'OmetricsAI_Roots'
const API_KEY = 'OmetricsAI_API'

const INLINE_HINT_KEYS = [
  'sendWidgetOpenedWhisper',
  'widgetOpenedWhisperData',
  'forceWidgetOpenedWhisper',
  'launchConversationId',
  'triggerConfig',
  'layout',
  'previewMode',
  'widget_bubble_color',
  'widget_bubble_background_color',
  'defaultCardImage',
  'floatingPosition',
  'floatingMarginTop',
  'floatingMarginBottom',
  'floatingMarginLeft',
  'floatingMarginRight',
  'slideOutPanelEdge',
  'slideOutPanelWidth',
  'slideOutPanelMaxHeight',
  'maxSuggestions',
  'position',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
]

/**
 * Preview / legacy embeds set `window.OmetricsAI_<id>` with launch-whisper
 * and bubble-icon fields that are not returned by `GET /api/v1/widget/config`.
 * After a successful remote config fetch, layer those hints back in.
 */
function applyLegacyInlineHints(conf, inline) {
  if (!inline || typeof inline !== 'object') return conf
  const next = { ...conf }
  for (const key of INLINE_HINT_KEYS) {
    if (inline[key] !== undefined) next[key] = inline[key]
  }
  if (!next.defaultCardImage) {
    const candidate =
      inline.defaultCardImage ||
      inline.configuration?.defaultCardImage ||
      inline.shared?.defaultCardImage ||
      ''
    if (candidate) next.defaultCardImage = String(candidate)
  }
  if (!next.customCss) {
    const candidate =
      inline.customCss ||
      inline.configuration?.customCss ||
      inline.shared?.customCss ||
      ''
    if (candidate) next.customCss = String(candidate)
  }
  const bubbleSvg = sanitizeWidgetBubbleIconSvg(inline.widget_bubble_icon)
  if (bubbleSvg) {
    next.widgetBubbleIcon = bubbleSvg
  }
  return next
}

/**
 * Body-anchored shadow host for injected slide-out/overlay panels.
 * Portaling here keeps `position: fixed` relative to the viewport instead
 * of a transformed in-page ancestor around the trigger mount.
 *
 * @param {string} instanceId
 * @param {object} conf
 * @param {{ themeOverrides?: object, customCss?: string }} [options]
 * @returns {{ panelHost: HTMLDivElement, panelContainer: HTMLDivElement, panelShadowRoot: ShadowRoot }}
 */
export function createInjectedViewportPanelHost(instanceId, conf, options = {}) {
  const { themeOverrides = {}, customCss = '' } = options
  const panelHost = document.createElement('div')
  panelHost.id = `ometrics-ai-panel-${instanceId}`
  panelHost.className = 'ometrics-ai-panel-host'
  document.body.appendChild(panelHost)
  startViewportOverflowAnchor(panelHost)

  const panelShadowRoot = panelHost.attachShadow({ mode: 'open' })
  const panelStyle = document.createElement('style')
  panelStyle.textContent = customCss ? `${widgetCss}\n${customCss}` : widgetCss
  panelShadowRoot.appendChild(panelStyle)

  const panelContainer = document.createElement('div')
  panelContainer.classList.add('widget-root')
  panelShadowRoot.appendChild(panelContainer)

  const template = resolveTemplate(conf)
  applyTemplateToHost(panelContainer, template, themeOverrides)
  applyConfThemeVarsToHost(panelContainer, conf)

  if (conf.fontFamily) {
    panelContainer.style.setProperty('--ometrics-font', conf.fontFamily)
    panelContainer.style.fontFamily = conf.fontFamily
  }

  return { panelHost, panelContainer, panelShadowRoot }
}

/**
 * Production entry point.
 *
 * Two ways to bootstrap:
 *   1. `mount(target, options)` - direct programmatic API, used by
 *      `loader.js` once it has resolved the manifest and pulled this
 *      hashed bundle in.
 *   2. Auto-bootstrap of containers matching `[id^="ometrics-ai-"]`
 *      with a matching `window.OmetricsAI_<instanceId>` config blob.
 *      This is the v1 contract and customers depend on it.
 *
 * Multi-instance is preserved end-to-end: each container gets its own
 * shadow root, its own React root, its own `OmetricsAI_API[id]` entry,
 * and its own widget config fetch.
 */

export async function mount(target, options = {}) {
  let host = resolveTarget(target)
  if (!host) {
    throw new Error('OmetricsAI widget mount target is missing')
  }
  prepareWidgetMountHost(host)

  let instanceId =
    options.instanceId || host.dataset.ometricsAiId || host.id.replace(/^ometrics-ai-/, '')

  if (host.shadowRoot) {
    const existingApi =
      typeof window !== 'undefined' && instanceId && window[API_KEY]?.[instanceId]
    const existingHandle =
      typeof window !== 'undefined' && instanceId && window[ROOTS_KEY]?.[instanceId]
    if (existingApi && existingHandle) {
      return existingHandle
    }

    console.warn(
      `[OmetricsAI] Replacing stale mount host #${host.id} (shadow root without API)`,
    )
    const parent = host.parentNode
    if (parent) {
      const replacement = document.createElement('div')
      replacement.id = host.id
      replacement.className = host.className
      for (const attr of host.attributes) {
        if (attr.name === 'id' || attr.name === 'class') continue
        replacement.setAttribute(attr.name, attr.value)
      }
      replacement.style.cssText = host.style.cssText
      parent.replaceChild(replacement, host)
      host = replacement
    }
  }

  instanceId = options.instanceId || host.dataset.ometricsAiId || generateUuid()
  host.dataset.ometricsAiId = instanceId
  startViewportOverflowAnchor(host)

  const inlineConfig =
    options.config ??
    (typeof window !== 'undefined'
      ? window[`${GLOBAL_KEY}_${instanceId}`] || window[GLOBAL_KEY]
      : null)

  const isPreviewInline =
    inlineConfig?.previewMode === true ||
    inlineConfig?.previewMode === 'true' ||
    inlineConfig?.previewMode === 1

  let configPayload = inlineConfig
  let mergedFromRemote = false
  // Preview pages embed full agent/theme/launch data from Laravel; fetching
  // /api/v1/widget/config with the preview iframe URL replaces that payload
  // and often drops colors, title, and launch conversation (page targeting).
  if (!options.skipFetch && !isPreviewInline) {
    try {
      const result = await fetchWidgetConfig({
        apiBaseUrl: options.apiBaseUrl,
        agentId: options.agentId,
        masterKey: options.masterKey,
        inlineConfig,
      })
      configPayload = result.payload
      mergedFromRemote = result.source !== 'inline'
      if (mergedFromRemote) {
        tryScheduleActiveSessionTracking({
          apiBaseUrl: options.apiBaseUrl,
          masterKey: options.masterKey,
          configPayload,
        })
      }
    } catch (error) {
      // Fallback: render with whatever inline config we have so the
      // widget at least surfaces an error toast instead of silently
      // failing on customer pages.
      console.error('OmetricsAI: failed to fetch widget config', error)
      if (!configPayload) throw error
    }
  }

  let conf = normalizeConfig(configPayload, {
    apiBaseUrl: options.apiBaseUrl,
    masterKey: options.masterKey,
    // v1 uses viewport width (768) for layout/trigger device — not UA "tablet",
    // which incorrectly forced slide-out → fullscreen on iPad landscape.
    isMobile: resolveIsMobile(inlineConfig),
  })
  if (mergedFromRemote && inlineConfig && typeof inlineConfig === 'object') {
    conf = applyLegacyInlineHints(conf, inlineConfig)
  }

  conf = bindVisitorIdToConf(conf)

  const shadowRoot = host.attachShadow({ mode: 'open' })

  const customCss = sanitizeCustomCss(conf.customCss || '')
  const style = document.createElement('style')
  style.textContent = customCss ? `${widgetCss}\n${customCss}` : widgetCss
  shadowRoot.appendChild(style)

  const shadowContainer = document.createElement('div')
  shadowContainer.classList.add('widget-root')
  shadowRoot.appendChild(shadowContainer)

  const template = resolveTemplate(conf)
  applyTemplateToHost(shadowContainer, template, options.themeOverrides)
  applyConfThemeVarsToHost(shadowContainer, conf)

  if (conf.fontFamily) {
    shadowContainer.style.setProperty('--ometrics-font', conf.fontFamily)
    shadowContainer.style.fontFamily = conf.fontFamily
  }

  let panelHost = null
  let panelContainer
  if (String(conf?.kind || '').toLowerCase() === 'injected') {
    const viewportPanel = createInjectedViewportPanelHost(instanceId, conf, {
      themeOverrides: options.themeOverrides,
      customCss,
    })
    panelHost = viewportPanel.panelHost
    panelContainer = viewportPanel.panelContainer
  }

  let publishedApi = null
  let resolveApiReady = () => {}
  const apiReadyPromise = new Promise((resolve) => {
    resolveApiReady = resolve
  })
  const onReady = (api) => {
    publishedApi = api
    if (typeof window !== 'undefined') {
      const registry = (window[API_KEY] = window[API_KEY] || {})
      registry[instanceId] = api
    }
    resolveApiReady(api)
  }

  const root = createRoot(shadowContainer)
  root.render(
    createElement(App, {
      conf,
      instanceId,
      apiBaseUrl: options.apiBaseUrl || conf.apiBaseUrl,
      container: shadowContainer,
      panelContainer,
      onReady,
      onSubmitForm: options.onSubmitForm,
    }),
  )

  await apiReadyPromise

  const handle = {
    instanceId,
    host,
    shadowRoot,
    panelHost,
    get api() {
      return publishedApi
    },
    unmount() {
      stopViewportOverflowAnchor(host)
      stopViewportOverflowAnchor(panelHost)
      try {
        root.unmount()
      } catch {
        // ignore unmount races
      }
      try {
        host.removeChild(shadowRoot)
      } catch {
        // shadow roots can't actually be removed manually; leave the
        // host element intact and let the customer DOM script tag
        // teardown reclaim it on the next nav.
      }
      if (panelHost?.parentNode) {
        panelHost.parentNode.removeChild(panelHost)
      }
      if (typeof window !== 'undefined') {
        const registry = window[API_KEY]
        if (registry && registry[instanceId] === publishedApi) {
          delete registry[instanceId]
        }
        const roots = window[ROOTS_KEY]
        if (roots) delete roots[instanceId]
      }
    },
  }

  if (typeof window !== 'undefined') {
    const roots = (window[ROOTS_KEY] = window[ROOTS_KEY] || {})
    roots[instanceId] = handle
  }
  return handle
}

function resolveTarget(target) {
  if (typeof target === 'string') {
    return document.querySelector(target)
  }
  if (target && target.nodeType === 1) return target
  return null
}

/**
 * Auto-bootstrap pass. Scans the page for `[id^="ometrics-ai-"]`
 * containers and mounts each. The legacy `OmetricsAI_Initialize`
 * helper is kept around for SPAs that re-render the host element and
 * want to force a remount.
 */
export async function autoBootstrap(options = {}) {
  if (typeof document === 'undefined') return []
  const containers = document.querySelectorAll('[id^="ometrics-ai-"]')
  const handles = []
  for (const container of containers) {
    const instanceId = container.id.replace(/^ometrics-ai-/, '')
    if (!instanceId) continue
    const inlineConfig =
      window[`${GLOBAL_KEY}_${instanceId}`] || window[GLOBAL_KEY] || null
    handles.push(
      await mount(container, {
        instanceId,
        config: inlineConfig,
        apiBaseUrl: options.apiBaseUrl,
        agentId: inlineConfig?.agentId || inlineConfig?.ometricsAI?.ometricsAiId,
        masterKey: inlineConfig?.masterKey,
        skipFetch:
          inlineConfig?.previewMode === true ||
          inlineConfig?.previewMode === 'true' ||
          inlineConfig?.previewMode === 1 ||
          (!options.apiBaseUrl && !inlineConfig?.streaming),
      }),
    )
  }
  return handles
}

if (typeof window !== 'undefined') {
  // Loader (`loader.js`) resolves the bundle with `import(manifestUrl)` then
  // reads `bundleModule.mount ?? window.OmetricsAI?.mount`. Rolldown can omit
  // named exports from the entry namespace, so we always publish the API on
  // `window.OmetricsAI` when the bundle evaluates (after the loader's minimal
  // `Object.assign(..., { loaderVersion })` stub).
  window.OmetricsAI = Object.assign(window.OmetricsAI ?? {}, {
    mount,
    autoBootstrap,
    fetchWidgetConfig,
    widgetConfigDeviceType,
    buildUnifiedMountQueue,
    mergeEnvelopeWithInstance,
    prepareWidgetMountHost,
    shouldSkipWidgetMountForPayload,
    tryScheduleActiveSessionTracking,
  })

  // Publish v1-compatible globals. Customer integrations call
  // `window.OmetricsAI_Initialize(instanceId)` after dynamic DOM
  // updates; we keep that contract.
  window.OmetricsAI_Initialize = (instanceId, options = {}) => {
    const target =
      typeof instanceId === 'string'
        ? document.getElementById(`ometrics-ai-${instanceId}`)
        : instanceId
    return mount(target, { ...options, instanceId })
  }
  window.OmetricsAI_AutoBootstrap = autoBootstrap

  // Floating-button visibility helpers. v1 exposed these as no-args
  // page-level functions; we forward to whichever instances are
  // currently mounted.
  window.OmetricsAI_HideFloatingWidget = () => {
    const registry = window[API_KEY] || {}
    Object.values(registry).forEach((api) => api?.hide?.())
  }
  window.OmetricsAI_ShowFloatingWidget = () => {
    const registry = window[API_KEY] || {}
    Object.values(registry).forEach((api) => api?.show?.())
  }
}

export {
  fetchWidgetConfig,
  widgetConfigDeviceType,
  buildUnifiedMountQueue,
  mergeEnvelopeWithInstance,
  prepareWidgetMountHost,
  shouldSkipWidgetMountForPayload,
  tryScheduleActiveSessionTracking,
}

export default mount
